// src/pages/About.js
import { FaExternalLinkAlt, FaGithub } from "react-icons/fa";
import { SiFiverr } from "react-icons/si";
import { useTeamPresence } from "../hooks/useTeamPresence";

// Single list; split into Active Team and Alumni & Contributors below.
const team = [
  {
    name: "FeaKH",
    role: "Founder & Project Lead",
    primaryBadge: "Owner",
    badgeTone: "owner",
    bio: "Founder and project lead overseeing product direction, infrastructure, architecture, and the long-term development of Afterparty Bot Labs.",
    image: "/images/feakh.png",
    discordId: "157891853625065472",
    github: "feakX",
  },
  {
    name: "mcst3rx",
    role: "Community Operations & Development",
    primaryBadge: "Community Manager",
    badgeTone: "community",
    bio: "Community manager and developer building Discord bots, automation systems, website features, and community tools across the Afterparty Bot Labs network.",
    image: "/images/mcst3rx.png",
    discordId: "201439668854718464",
    github: "mcst3rx",
  },
  {
    name: "n1cho",
    role: "DISCORD BOT DEVELOPMENT",
    primaryBadge: "Contract Developer",
    badgeTone: "contract",
    bio: "Contract developer contributing to Discord bot projects across Afterparty Bot Labs, including music, automation, and community-focused systems.",
    image: "/images/n1cho.png",
    discordId: "731477352101707807",
    github: "notN1CHO",
    fiverr: "samusken1cho123",
    allowLinks: true,
  },

  // Alumni & Contributors
  {
    name: "cyberblad3x",
    role: "Automation & Moderation",
    primaryBadge: "Retired Developer",
    badgeTone: "retired",
    status: "retired",
    allowLinks: true,
    bio: "Former bot developer who contributed to automation systems, moderation tools, and user experience improvements.",
    image: "/images/cyberblad3x.png",
    discordId: "456226577798135808",
    github: "cyberblad3x",
  },
  {
    name: "Blockzilla101",
    role: "CONTRACT DEVELOPMENT",
    primaryBadge: "Former Contract Developer",
    badgeTone: "formerContract",
    status: "contractor",
    allowLinks: true,
    bio: "Former contract developer who supported selected Afterparty Bot Labs development projects.",
    image: "https://github.com/Blockzilla101.png",
    github: "Blockzilla101",
    discordId: "429215360957415424",
    fiverr: "blockzilla",
  },
  {
    name: "Diwas Atreya",
    role: "CONTRACT DEVELOPMENT",
    primaryBadge: "Former Contract Developer",
    badgeTone: "formerContract",
    status: "contractor",
    allowLinks: true,
    bio: "Former contract developer who supported selected Afterparty Bot Labs development projects.",
    image: "https://github.com/diwasatreya.png",
    github: "diwasatreya",
    discordId: "519666024220721152",
    fiverr: "diptiatreya",
  },
];

const BADGE_TONES = {
  owner: "border-apb-accent-purple/35 bg-gradient-to-r from-apb-accent-purple to-apb-accent-cyan text-white apb-glow-text",
  community: "border-apb-accent-cyan/30 bg-gradient-to-r from-apb-accent-purple/70 to-apb-accent-cyan/70 text-white apb-glow-cyan",
  contract: "border-apb-accent-cyan/30 bg-gradient-to-r from-[#2dd4bf] to-apb-accent-cyan text-white apb-glow-text",
  formerContract: "border-apb-accent-cyan/30 bg-gradient-to-r from-[#2dd4bf] to-apb-accent-cyan text-white apb-glow-text",
  retired: "border-white/15 bg-white/[0.07] text-apb-text-secondary",
};

function PrimaryBadge({ children, tone }) {
  return (
    <span
      className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold shadow-sm ${ 
        BADGE_TONES[tone]
      }`}
    >
      {children}
    </span>
  );
}

const PRESENCE_LABELS = {
  online: { label: "Online", dotClass: "bg-[#23a559]", textClass: "text-[#57f287]" },
  idle: { label: "Idle", dotClass: "bg-[#f0b232]", textClass: "text-[#f0b232]" },
  dnd: { label: "Do Not Disturb", dotClass: "bg-[#f23f42]", textClass: "text-[#f23f42]" },
  unavailable: { label: "Unavailable", dotClass: "bg-[#80848e]", textClass: "text-[#b5bac1]" },
};

function TeamPresence({ member, teamPresence }) {
  if (member.status || !teamPresence) {
    return null;
  }

  const presence = PRESENCE_LABELS[teamPresence[member.name]] || PRESENCE_LABELS.unavailable;

  return (
      <span
      className={`absolute -bottom-0.5 -right-0.5 h-5 w-5 rounded-full border-[3px] border-apb-bg-card ring-1 ring-apb-accent-cyan/40 shadow-sm ${presence.dotClass}`}
      role="img"
      aria-label={`Discord status: ${presence.label}`}
    />
  );
}
function TeamCard({ member, teamPresence }) {
  const isRetired = member.status === "retired";
  const isContractor = member.status === "contractor";
  const isInactive = isRetired || isContractor;
  const canShowLinks = !isInactive || member.allowLinks === true;

  const hostLabel = member.website
    ? new URL(member.website).hostname.replace("www.", "")
    : null;

    return (
    <article
      className={[
        "relative rounded-2xl p-6 apb-surface-glass transition duration-240 flex flex-col items-center text-center group border border-apb-border-soft",
        isInactive
          ? "opacity-85 hover:opacity-100 hover:bg-apb-bg-panel/75"
          : "hover:border-apb-accent-cyan/60 hover:apb-glow-cyan hover:-translate-y-1",
      ].join(" ")}
    >
      <div className="relative">
        <img
          src={member.image}
          alt={`${member.name} avatar`}
          loading="lazy"
          className={[
            "w-28 h-28 rounded-full border-4 object-cover shadow-lg transition-transform duration-240 group-hover:scale-105",
            isInactive ? "border-apb-border-soft grayscale hover:grayscale-0" : "border-apb-accent-purple ring-4 ring-apb-accent-cyan/25",
          ].join(" ")}
          onError={(event) => {
            event.currentTarget.src = "/images/team-logo.png";
          }}
        />
        <TeamPresence member={member} teamPresence={teamPresence} />
      </div>

      <h2 className="mt-5 text-2xl font-bold apb-glow-text">{member.name}</h2>
      <h3 className="mt-1 text-sm font-medium uppercase tracking-wide text-apb-text-secondary">{member.role}</h3>

      <div className="mt-3 flex min-h-[28px] items-center justify-center">
        <PrimaryBadge tone={member.badgeTone}>{member.primaryBadge}</PrimaryBadge>
      </div>

      <p className="mt-4 flex-grow text-sm leading-relaxed text-apb-text-secondary">{member.bio}</p>

      <div className="mt-6 flex w-full flex-wrap items-center justify-center gap-x-4 gap-y-3 border-t border-apb-border-soft pt-4">
        {member.discordId && canShowLinks && (
          <a
            href={`https://discord.com/users/${member.discordId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded px-2 py-1 font-semibold text-apb-accent-cyan transition hover:bg-white/10 focus-visible:outline-none focus-ring-neo"
            aria-label={`Contact ${member.name} on Discord`}
          >
            <FaExternalLinkAlt className="text-xs" /> Discord
          </a>
        )}

        {member.github && canShowLinks && (
          <a
            href={`https://github.com/${member.github}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded px-2 py-1 font-semibold text-apb-text-secondary transition hover:bg-white/10 hover:text-white focus-visible:outline-none focus-ring-neo"
            aria-label={`${member.name}'s GitHub`}
          >
            <FaGithub className="text-base" /> GitHub
          </a>
        )}

        {member.fiverr && canShowLinks && (
          <a
            href={`https://www.fiverr.com/${member.fiverr}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded px-2 py-1 font-semibold text-apb-accent-cyan transition hover:bg-white/10 hover:text-white focus-visible:outline-none focus-ring-neo"
            aria-label={`${member.name}'s Fiverr`}
          >
            <SiFiverr className="text-base" /> Fiverr
          </a>
        )}

        {member.website && canShowLinks && (
          <a
            href={member.website}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded px-2 py-1 font-semibold text-apb-text-secondary transition hover:bg-white/10 hover:text-white focus-visible:outline-none focus-ring-neo"
            aria-label={`${member.name}'s external profile`}
          >
            <FaExternalLinkAlt className="text-xs" /> {hostLabel}
          </a>
        )}
      </div>
    </article>
  );
}

export default function About() {
  const teamPresence = useTeamPresence();
  const active = team.filter((member) => !["retired", "contractor"].includes(member.status));
  const alumni = team.filter((member) => ["retired", "contractor"].includes(member.status));

  return (
    <div className="relative min-h-full py-16 px-4">
      <div className="pointer-events-none absolute inset-x-0 top-6 left-1/2 h-px w-[80%] -translate-x-1/2 bg-gradient-to-r from-transparent via-apb-accent-cyan to-transparent opacity-70" />
      <div className="pointer-events-none absolute inset-x-0 top-16 left-0 h-full w-full">
        <div className="mx-auto h-full max-w-6xl bg-[linear-gradient(to_right,transparent_0%,rgba(255,255,255,0.06)_2px,transparent_2px,transparent_52px),linear-gradient(to_bottom,transparent_0%,rgba(255,255,255,0.06)_2px,transparent_2px,transparent_52px)] bg-[length:52px_52px,52px_52px] opacity-10" />
      </div>
      <div className="max-w-6xl mx-auto text-center relative z-10">
        <header className="mb-12 apb-surface-glass px-6 py-8 rounded-2xl">
          <p className="inline-flex items-center gap-2 rounded-full border border-apb-border-soft bg-black/20 px-4 py-1.5 text-xs uppercase tracking-[0.25em] text-apb-text-secondary">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-apb-accent-cyan apb-glow-cyan" />
            About the Mission
          </p>
          <h1 className="mt-5 text-4xl md:text-5xl font-extrabold mb-6 apb-glow-text">About Us</h1>
          <p className="max-w-3xl mx-auto text-lg text-apb-text-secondary">
            Afterparty Bot Labs builds and maintains Discord bots for music, moderation, verification, automation, and community tools.
          </p>
          <p className="max-w-3xl mx-auto mt-3 text-apb-text-secondary">
            We create practical tools that help communities stay active, organized, and welcoming.
          </p>
        </header>

        <h2 className="text-2xl md:text-3xl font-bold text-left mb-4 mt-12 text-white apb-glow-text pl-2 border-l-4 border-apb-accent-purple">Active Team</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 place-items-center mb-12">
          {active.map((member) => (
            <TeamCard key={member.name} member={member} teamPresence={teamPresence} />
          ))}
        </div>

        {alumni.length > 0 && (
          <>
            <h2 className="text-2xl md:text-3xl font-bold text-left mb-4 text-white apb-glow-text pl-2 border-l-4 border-apb-accent-cyan">
              Alumni &amp; Contributors
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 place-items-center">
              {alumni.map((member) => (
                <TeamCard key={member.name} member={member} teamPresence={teamPresence} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}













